// Persists navigation routes (profile + coordinates) to Supabase so the
// world map can overlay past journeys as colored polylines, synced across
// all of the user's devices:
//   driving = blue, foot (walked) = red, bike (cycled) = green.
//
// Each route is downsampled client-side to keep the JSONB row small.

import { supabase } from '@/lib/supabaseClient';
import { downsample } from '@/lib/geoUtils';
import { getMemorySnapshot } from '@/lib/memoryGuard';

const PAGE_SIZE = 1000;
let navigationPathsPromise = null;

let _routesReadyResolve;
export const routesReadyPromise = new Promise((resolve) => {
  _routesReadyResolve = resolve;
});
export function notifyRoutesReady() {
  _routesReadyResolve?.();
  _routesReadyResolve = null;
}

// Load the current user's navigation paths (newest first).
export function loadNavigationPaths({ force = false } = {}) {
  if (navigationPathsPromise && !force) return navigationPathsPromise;
  navigationPathsPromise = (async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) return [];
      const lowMemory = getMemorySnapshot().low;
      const maxRoutes = lowMemory ? 100 : Infinity;
      const maxPoints = lowMemory ? 250 : Infinity;
      const pageSize = lowMemory ? 100 : PAGE_SIZE;
      const paths = [];
      let offset = 0;
      while (true) {
        const { data, error } = await supabase
          .from('navigation_paths')
          .select('profile, coordinates, created_at')
          .eq('is_tracked', true) // only actual GPS-tracked trips, not old planned routes
          .order('created_at', { ascending: false })
          .range(offset, offset + pageSize - 1);
        if (error) return [];
        paths.push(...(data || []));
        if (!data || data.length < pageSize || paths.length >= maxRoutes) break;
        offset += pageSize;
      }
      return paths
        .slice(0, maxRoutes)
        .map((r) => ({
          profile: r.profile,
          coordinates: Array.isArray(r.coordinates)
            ? downsample(r.coordinates.filter((point) => Array.isArray(point) && point.length >= 2 && Number.isFinite(point[0]) && Number.isFinite(point[1])), maxPoints)
            : [],
          date: new Date(r.created_at).getTime(),
        }))
        .filter((r) => r.coordinates.length >= 2 && Number.isFinite(r.date));
    } catch {
      return [];
    }
  })();
  return navigationPathsPromise;
}

// Start a new tracked trip (actual GPS path, not a planned route).
// Inserts a new row and returns its ID for later appendTrackedTrip calls.
export async function startTrackedTrip(profile, coordinates) {
  if (!coordinates || coordinates.length < 2) return null;
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    if (!user) return null;
    const { data, error } = await supabase.from('navigation_paths').insert({
      user_id: user.id,
      profile: profile || 'driving',
      coordinates: downsample(coordinates, 400),
      is_tracked: true, // marks this as a real GPS-tracked trip, not a planned route
    }).select('id').single();
    if (error) return null;
    window.dispatchEvent(new Event('nav-paths-changed'));
    return data?.id || null;
  } catch {
    return null;
  }
}

// Append coordinates to an existing tracked trip (updates the row in place
// so one continuous trip = one row, regardless of duration).
export async function appendTrackedTrip(tripId, coordinates, profile) {
  if (!tripId || !coordinates || coordinates.length < 2) return false;
  try {
    const { data: existing, error: fetchError } = await supabase
      .from('navigation_paths')
      .select('coordinates')
      .eq('id', tripId)
      .single();
    if (fetchError) return false;
    const previous = Array.isArray(existing?.coordinates) ? existing.coordinates : [];
    const updateData = { coordinates: downsample([...previous, ...coordinates], 400) };
    if (profile) updateData.profile = profile;
    const { error } = await supabase.from('navigation_paths')
      .update(updateData)
      .eq('id', tripId);
    if (error) return false;
    window.dispatchEvent(new Event('nav-paths-changed'));
    return true;
  } catch {
    return false;
  }
}